/**
 * FollowUpsPage (N4a) — a work queue with two independent view controls (both
 * URL-synced, owned by App): WHOSE follow-ups (Mine · a user · All) and WHICH day
 * (Today · a specific date · All). Default is Mine · Today.
 *
 * Today view sections: Overdue → Today's Follow-Ups (untimed first, then timed
 * chronological) → Completed Today (collapsed). A specific date shows that day's
 * follow-ups with no Overdue section. "All" is a running chronological list of
 * every outstanding follow-up, overdue first.
 *
 * Rows reuse ClientCells for client detail (shared with All Clients); the
 * follow-up-specific parts (date/time, assignee chip, Mark complete) live here.
 * Badge counts are unaffected by these controls (a separate personal endpoint).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CRM } from '../lib/theme';
import { gate } from './States';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useApiClient } from '../hooks/useApi';
import { formatDate } from '../lib/utils';
import { userTz, todayInTz, shortTime } from '../lib/time';
import { Icons } from '../lib/icons';
import { ClientCells, ClientField } from './ClientCells';
import { DayTimetable } from './DayTimetable';
import { leadsOfFollowUps } from '../lib/followUpLeads';
import { useUrlParam } from '../hooks/useListState';
import { ClearFilters } from './LeadsPage';

const C = { ...CRM, mono: "'DM Mono', monospace" };

// The client-detail columns carried by a follow-up row. Deliberately minimal so
// the row fits the two-thirds list column beside the timetable: just job# + name
// (name-only variant, no email). The follow-up's own date/time, assignee chip and
// Mark-complete live in the row itself; phone/type/email stay on All Clients.
const ROW_FIELDS: ClientField[] = ['job', 'clientName'];

interface FollowUpsPageProps {
  onSelectLead?: (leadId: string) => void;
  who: string;   // 'mine' | userId | 'all'
  when: string;  // 'today' | 'all' | 'YYYY-MM-DD'
  onChangeView: (who: string, when: string) => void;
}

const selectStyle: React.CSSProperties = {
  padding: '6px 9px', background: C.panel2, border: `1px solid ${C.border}`, borderRadius: '7px', color: C.text, fontSize: '12px', cursor: 'pointer',
};
const segBtn = (active: boolean): React.CSSProperties => ({
  padding: '6px 12px', background: active ? C.accentFill : C.panel2, border: `1px solid ${active ? C.accentLine : C.border}`, color: active ? 'var(--crm-on-fill)' : C.muted,
  borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
});

export function FollowUpsPage({ onSelectLead, who, when, onChangeView }: FollowUpsPageProps) {
  const client = useApiClient();
  const { user } = useAuth();
  const tz = userTz(user);

  // 'mine' → personal (no param); a user id or 'all' → the ?assignee= view.
  const assignee = who === 'mine' ? undefined : who;
  const { data: followupsData, loading: fuLoading, error: fuError, refetch } = useApi<any[]>(
    async (api) => (await api.getFollowUps(assignee)).data || [],
    { dependencies: [who] }
  );
  const { data: usersData } = useApi<any[]>(async (api) => (await api.getUsers()).data || []);

  // Who and when are App's (?who=, ?when=); the open "Completed today" section
  // is kept in the URL too (?done=1), so it is still open on the way back.
  const [doneParam, setDoneParam] = useUrlParam('done');
  const showCompleted = doneParam === '1';
  const [busyId, setBusyId] = useState<string | null>(null);

  // Two-thirds list / one-third sticky timetable, stacking below 1024px (N4b).
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  useEffect(() => {
    const on = () => setNarrow(window.innerWidth < 1024);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const today = todayInTz(tz);
  const followups = followupsData || [];
  const users = usersData || [];
  // Each follow-up carries its lead (paging 3/4) — no full lead list to load.
  const leadById = useMemo(() => leadsOfFollowUps(followups), [followups]);

  const assigneeName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email : '';
  };

  // Untimed first, then timed chronological (within a day).
  const byTime = (a: any, b: any) => {
    if (!a.follow_up_time && b.follow_up_time) return -1;
    if (a.follow_up_time && !b.follow_up_time) return 1;
    return String(a.follow_up_time || '').localeCompare(String(b.follow_up_time || ''));
  };

  const open = useMemo(() => followups.filter((f) => !f.completed), [followups]);

  const isToday = when === 'today';
  const isAll = when === 'all';
  const specificDate = !isToday && !isAll ? when : '';

  const overdue = useMemo(() => open.filter((f) => f.follow_up_date < today).sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date) || byTime(a, b)), [open, today]);
  const todayList = useMemo(() => open.filter((f) => f.follow_up_date === today).sort(byTime), [open, today]);
  const dateList = useMemo(() => open.filter((f) => f.follow_up_date === specificDate).sort(byTime), [open, specificDate]);
  const allList = useMemo(() => [...open].sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date) || byTime(a, b)), [open]);
  const completedToday = useMemo(
    () => followups.filter((f) => f.completed && f.completed_at && todayInTz(tz, new Date(f.completed_at)) === today),
    [followups, today, tz]
  );

  // N4b timetable: follows the page's date selector ("All" shows today), and
  // whose list is shown. Reschedule writes through the existing updateFollowUp
  // path; the DayTimetable does the optimistic + snap-back locally.
  const ttDate = isToday || isAll ? today : specificDate;
  const ttEditable = ttDate >= today; // today/future only — past is read-only
  const ttOwnerName = who !== 'mine' && who !== 'all' ? assigneeName(who) : undefined;
  const ttOwnerTz = who !== 'mine' && who !== 'all' ? users.find((u) => u.id === who)?.timezone : undefined;
  const reschedule = useCallback(
    async (fuId: string, patch: { follow_up_date: string; follow_up_time: string | null }) => {
      await client.updateFollowUp(fuId, patch);
      await refetch();
    },
    [client, refetch]
  );

  const complete = async (fuId: string) => {
    setBusyId(fuId);
    try {
      await client.completeFollowUp(fuId);
      await refetch();
    } catch (err: any) {
      window.alert(`Couldn't mark this follow-up complete: ${err?.message || 'network error'}. Please try again.`);
    } finally {
      setBusyId(null);
    }
  };

  const row = (fu: any, opts: { showDate?: boolean; overdue?: boolean; done?: boolean } = {}) => {
    const lead = leadById.get(fu.lead_id) ?? { id: fu.lead_id, first_name: 'Unknown', last_name: '' };
    const mine = fu.assigned_to_user_id === user?.id;
    const left = opts.showDate || opts.overdue
      ? formatDate(fu.follow_up_date)
      : (fu.follow_up_time ? shortTime(fu.follow_up_time) : 'Untimed');
    return (
      <div
        key={fu.id}
        onClick={() => onSelectLead?.(fu.lead_id)}
        style={{ display: 'flex', alignItems: 'center', gap: '12px', background: C.panel, border: `1px solid ${opts.overdue ? C.redSoftLine : C.border}`, borderRadius: '9px', padding: '9px 12px', marginBottom: '7px', cursor: onSelectLead ? 'pointer' : 'default', opacity: opts.done ? 0.65 : 1 }}
      >
        <div style={{ width: 92, flex: '0 0 92px', fontSize: '12px', fontFamily: C.mono, color: opts.overdue ? C.red : C.amber }}>{left}</div>
        <ClientCells lead={lead} users={users} fields={ROW_FIELDS} viewerId={user?.id} tz={tz} />
        <div style={{ flex: '1 1 120px', minWidth: 80, fontSize: '11px', color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fu.description || 'No notes'}</div>
        {!mine && <span style={{ fontSize: '9px', fontWeight: 700, color: C.accent, background: `${C.accentTint}`, padding: '1px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>{assigneeName(fu.assigned_to_user_id)}</span>}
        <div onClick={(e) => e.stopPropagation()} style={{ flex: '0 0 auto' }}>
          {opts.done ? (
            <span style={{ fontSize: '10px', color: C.dim, display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icons.check size={12} /> done</span>
          ) : (
            <button onClick={() => complete(fu.id)} disabled={busyId === fu.id} style={{ padding: '6px 12px', background: C.panel2, border: `1px solid ${C.greenSoftLine}`, color: C.green, borderRadius: '6px', cursor: busyId === fu.id ? 'default' : 'pointer', fontSize: '11px', fontWeight: 600, opacity: busyId === fu.id ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icons.check size={13} /> Mark complete</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  const sectionH = (label: string, color: string, id?: string) => (
    <h3 id={id} style={{ fontSize: '11px', fontWeight: 700, color, textTransform: 'uppercase', margin: '18px 0 10px', letterSpacing: '.5px' }}>{label}</h3>
  );

  const viewingOther = who !== 'mine';

  return (
    <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
      {/* "Nothing due today" is the best news this page can give, and it was the
          exact picture a failed request produced. */}
      {gate('Follow-ups', fuLoading, fuError, !!followupsData, refetch)}

      {/* View controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Follow-Ups</h1>
        {/* Whose */}
        <select value={who} onChange={(e) => onChangeView(e.target.value, when)} style={selectStyle} aria-label="Whose follow-ups">
          <option value="mine">Mine</option>
          <option value="all">All users</option>
          {users.map((u) => <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email}</option>)}
        </select>
        {/* Which day */}
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <button style={segBtn(isToday)} onClick={() => onChangeView(who, 'today')}>Today</button>
          <button style={segBtn(isAll)} onClick={() => onChangeView(who, 'all')}>All</button>
          <input type="date" value={specificDate} onChange={(e) => onChangeView(who, e.target.value || 'today')} style={{ ...selectStyle, colorScheme: 'dark' }} aria-label="Specific date" />
        </div>
        <ClearFilters
          active={who !== 'mine' || when !== 'today' || showCompleted}
          onClear={() => { setDoneParam(''); onChangeView('mine', 'today'); }}
        />
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: '12px', color: C.dim }}>{formatDate(today)}</div>
      </div>

      {/* "Not yours" banner */}
      {viewingOther && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--crm-accent-tint)', border: `1px solid ${C.accentSoftLine}`, color: C.accent, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', marginBottom: '12px' }}>
          Viewing {who === 'all' ? 'all users’' : `${assigneeName(who)}’s`} follow-ups — not your own list
        </div>
      )}

      {/* Split: list (two-thirds) + sticky day timetable (one-third), stacking
          below 1024px. minWidth:0 + overflow:hidden hard-bound the list column so
          rows can't run under the timetable panel; the gap keeps clear space. */}
      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexDirection: narrow ? 'column' : 'row' }}>
        <div style={{ flex: narrow ? '1 1 auto' : '2 1 0', minWidth: 0, width: narrow ? '100%' : 'auto', overflow: narrow ? 'visible' : 'hidden' }}>
      {/* ===== TODAY view ===== */}
      {isToday && (
        <>
          {sectionH(`Overdue · ${overdue.length}`, overdue.length > 0 ? C.red : C.muted, 'overdue')}
          {overdue.length > 0 ? overdue.map((fu) => row(fu, { overdue: true })) : <div style={{ fontSize: '12px', color: C.dim, marginBottom: '8px' }}>Nothing overdue.</div>}

          {sectionH(`Today's Follow-Ups · ${todayList.length}`, C.amber)}
          {todayList.length > 0 ? todayList.map((fu) => row(fu)) : <div style={{ fontSize: '12px', color: C.dim, marginBottom: '8px' }}>Nothing due today.</div>}
        </>
      )}

      {/* ===== SPECIFIC DATE view (no Overdue — only meaningful vs now) ===== */}
      {specificDate && (
        <>
          {sectionH(`${formatDate(specificDate)} · ${dateList.length}`, C.amber)}
          {dateList.length > 0 ? dateList.map((fu) => row(fu, { showDate: false })) : <div style={{ fontSize: '12px', color: C.dim }}>Nothing scheduled for this date.</div>}
        </>
      )}

      {/* ===== ALL view — every outstanding, overdue first ===== */}
      {isAll && (
        <>
          {sectionH(`All outstanding · ${allList.length}`, C.amber)}
          {allList.length > 0 ? allList.map((fu) => row(fu, { showDate: true, overdue: fu.follow_up_date < today })) : <div style={{ fontSize: '12px', color: C.dim }}>No outstanding follow-ups.</div>}
        </>
      )}

      {/* Completed today — only on the Today view. */}
      {isToday && (
        <div style={{ marginTop: '22px', borderTop: `1px solid ${C.border}`, paddingTop: '12px' }}>
          <button onClick={() => setDoneParam(showCompleted ? '' : '1')} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0 }}>
            {showCompleted ? <Icons.chevronDown size={14} /> : <Icons.chevronRight size={14} />} Completed today · {completedToday.length}
          </button>
          {showCompleted && (
            <div style={{ marginTop: '10px' }}>
              {completedToday.length > 0 ? completedToday.map((fu) => row(fu, { showDate: true, done: true })) : <div style={{ fontSize: '12px', color: C.dim }}>Nothing completed today yet.</div>}
            </div>
          )}
        </div>
      )}
        </div>

        {/* Right third — sticky day timetable */}
        <div style={{ flex: narrow ? '1 1 auto' : '1 1 0', minWidth: 0, width: narrow ? '100%' : 'auto', position: narrow ? 'static' : 'sticky', top: 0, alignSelf: 'flex-start' }}>
          <DayTimetable
            date={ttDate}
            followUps={open}
            users={users}
            leadById={leadById}
            viewer={user}
            tz={tz}
            gridOwnerName={ttOwnerName}
            gridTz={ttOwnerTz}
            editable={ttEditable}
            onSelectLead={onSelectLead}
            onReschedule={reschedule}
          />
        </div>
      </div>
    </div>
  );
}
